/**
 * The git-connections half of the KnowIt adapter: `git.connections.*`.
 *
 * Bespoke `{ data }` envelopes over the same bearer-injected transport as `knowitWork.ts` — a raw
 * `fetch` from a scene arrives anonymous. Errors keep the server's `{ ok:false, error, message }`
 * so the UI can name `secret_store_unavailable` or `credential_too_broad` rather than guess.
 */
import type {
  GitConnection,
  GitConnectionDraft,
  GitConnectionKind,
  GitConnectionList,
  GitConnectionPatch,
  GitConnectionStatus,
  GitFile,
  GitHubAppStatus,
  GitHubInstallation,
  GitHubRepository,
  GitIngestReport,
  GitProbe,
  GitTree,
  GitTreeEntry,
} from "~/api/gitTypes";

export interface GitTransport {
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
}

export interface GitApi {
  connections: {
    /** All of the workspace's, one project's, or (`scope: "workspace"`) only the workspace-level ones. */
    list(query?: { projectId?: string; scope?: "workspace" }): Promise<GitConnectionList>;
    create(input: GitConnectionDraft): Promise<GitConnection>;
    update(input: GitConnectionPatch): Promise<GitConnection>;
    /** Ask the provider (or the mount) what this connection can see. */
    test(id: string): Promise<GitProbe>;
    ingest(id: string, sinceDays?: number): Promise<GitIngestReport>;
    rotate(id: string, token: string): Promise<GitConnection>;
    remove(id: string): Promise<void>;
    /** One level of the structure, rooted at the prefix. `path` is connection-relative. */
    tree(id: string, path?: string): Promise<GitTree>;
    file(id: string, path: string): Promise<GitFile>;
  };
  github: {
    status(): Promise<GitHubAppStatus>;
    /** The GitHub page to install the app on; GitHub brings the person back to `returnTo`. */
    installStart(returnTo: string, link?: boolean): Promise<string>;
    installations(): Promise<GitHubInstallation[]>;
    repositories(installationId: string): Promise<GitHubRepository[]>;
  };
}

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" ? (value as Json) : {};
}
function arr(value: unknown): Json[] {
  return Array.isArray(value) ? value.map(obj) : [];
}
function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}
function strOrNull(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}
function num(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function bool(value: unknown): boolean {
  return value === true;
}

const KINDS: GitConnectionKind[] = ["mount", "public", "app", "deploy-key", "pat"];
const STATUSES: GitConnectionStatus[] = ["new", "ok", "failing", "disabled"];

export function toGitConnection(raw: Json): GitConnection {
  const kind = str(raw.kind);
  const status = str(raw.status);
  return {
    id: str(raw.id),
    projectId: strOrNull(raw.projectId),
    provider: str(raw.provider, "github"),
    kind: (KINDS as string[]).includes(kind) ? (kind as GitConnectionKind) : "mount",
    remoteUrl: str(raw.remoteUrl),
    defaultBranch: str(raw.defaultBranch, "main"),
    pathPrefix: strOrNull(raw.pathPrefix),
    hasCredential: bool(raw.hasCredential),
    installationId: strOrNull(raw.installationId),
    lastIngestedSha: strOrNull(raw.lastIngestedSha),
    lastIngestedAt: strOrNull(raw.lastIngestedAt),
    status: (STATUSES as string[]).includes(status) ? (status as GitConnectionStatus) : "new",
    lastError: strOrNull(raw.lastError),
    enabled: raw.enabled !== false,
    updatedAt: str(raw.updatedAt, new Date(0).toISOString()),
  };
}

function toEntry(raw: Json): GitTreeEntry {
  return {
    name: str(raw.name),
    path: str(raw.path),
    type: raw.type === "dir" ? "dir" : "file",
    size: typeof raw.size === "number" ? raw.size : null,
  };
}

function toTree(raw: Json): GitTree {
  return {
    connectionId: str(raw.connectionId),
    root: str(raw.root),
    path: str(raw.path),
    branch: str(raw.branch, "HEAD"),
    entries: arr(raw.entries).map(toEntry),
  };
}

function toFile(raw: Json): GitFile {
  return {
    connectionId: str(raw.connectionId),
    root: str(raw.root),
    path: str(raw.path),
    branch: str(raw.branch, "HEAD"),
    size: num(raw.size),
    truncated: bool(raw.truncated),
    binary: bool(raw.binary),
    content: typeof raw.content === "string" ? raw.content : null,
  };
}

function toProbe(raw: Json): GitProbe {
  return {
    ok: bool(raw.ok),
    error: strOrNull(raw.error),
    repository: strOrNull(raw.repository),
    latestSha: strOrNull(raw.latestSha),
    latestMessage: strOrNull(raw.latestMessage),
    permissions: Array.isArray(raw.permissions) ? raw.permissions.map(String) : null,
    canWrite: typeof raw.canWrite === "boolean" ? raw.canWrite : null,
  };
}

function toIngest(raw: Json): GitIngestReport {
  return {
    available: bool(raw.available),
    reason: strOrNull(raw.reason),
    commitsSeen: num(raw.commitsSeen),
    commitsAdded: num(raw.commitsAdded),
    sweeping: num(raw.sweeping),
  };
}

/** Drops undefined/empty optional strings so the server sees absent, not "". */
function compact(body: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body)) {
    if (value === undefined) continue;
    if (typeof value === "string" && value.trim() === "" && key !== "remoteUrl") continue;
    out[key] = typeof value === "string" ? value.trim() : value;
  }
  return out;
}

export function createKnowItGit(transport: GitTransport): GitApi {
  const post = async (method: string, body: Record<string, unknown> = {}) =>
    obj(await transport.post(method, body));
  return {
    connections: {
      list: async (query = {}) => {
        const envelope = await post("git.connections.list", {
          ...(query.projectId ? { projectId: query.projectId } : {}),
          ...(query.scope ? { scope: query.scope } : {}),
        });
        return {
          connections: arr(envelope.data).map(toGitConnection),
          secretsAvailable: bool(envelope.secretsAvailable),
        };
      },
      create: async (input) => {
        const envelope = await post(
          "git.connections.create",
          compact({
            projectId: input.projectId ?? undefined,
            kind: input.kind,
            remoteUrl: input.remoteUrl,
            provider: input.provider,
            defaultBranch: input.defaultBranch,
            pathPrefix: input.pathPrefix,
            token: input.token,
            installationId: input.installationId,
          }),
        );
        return toGitConnection(obj(envelope.data));
      },
      update: async (input) => {
        const envelope = await post("git.connections.update", {
          id: input.id,
          ...(input.defaultBranch !== undefined ? { defaultBranch: input.defaultBranch } : {}),
          ...(input.pathPrefix !== undefined ? { pathPrefix: input.pathPrefix } : {}),
          ...(input.enabled !== undefined ? { enabled: input.enabled } : {}),
        });
        return toGitConnection(obj(envelope.data));
      },
      test: async (id) => toProbe(obj((await post("git.connections.test", { id })).data)),
      ingest: async (id, sinceDays) =>
        toIngest(
          obj(
            (
              await post("git.connections.ingest", {
                id,
                ...(sinceDays ? { sinceDays } : {}),
              })
            ).data,
          ),
        ),
      rotate: async (id, token) =>
        toGitConnection(obj((await post("git.connections.rotate", { id, token })).data)),
      remove: async (id) => {
        await post("git.connections.remove", { id });
      },
      tree: async (id, path) =>
        toTree(obj((await post("git.connections.tree", { id, path: path ?? "" })).data)),
      file: async (id, path) =>
        toFile(obj((await post("git.connections.file", { id, path })).data)),
    },
    github: {
      status: async () => {
        const data = obj((await post("git.github.status")).data);
        return { available: bool(data.available), slug: strOrNull(data.slug) };
      },
      installStart: async (returnTo, link) =>
        str(
          obj((await post("git.github.installStart", { returnTo, link: link === true })).data).url,
        ),
      installations: async () =>
        arr((await post("git.github.installations")).data).map((i) => ({
          installationId: str(i.installationId),
          account: str(i.account),
          accountType: str(i.accountType),
        })),
      repositories: async (installationId) =>
        arr((await post("git.github.repositories", { installationId })).data).map((r) => ({
          fullName: str(r.fullName),
          url: str(r.url),
          defaultBranch: str(r.defaultBranch, "main"),
          private: bool(r.private),
        })),
    },
  };
}

/**
 * The offline client's git surface: nothing is connected, and nothing can be — the fixture
 * corpus has no repositories, and faking a tree would demonstrate a structure that is not real.
 */
export function createFakeGit(): GitApi {
  const refuse = async (): Promise<never> => {
    throw new Error("Git connections need the real API.");
  };
  return {
    connections: {
      list: async () => ({ connections: [], secretsAvailable: false }),
      create: refuse,
      update: refuse,
      test: refuse,
      ingest: refuse,
      rotate: refuse,
      remove: refuse,
      tree: refuse,
      file: refuse,
    },
    github: {
      status: async () => ({ available: false, slug: null }),
      installStart: refuse,
      installations: async () => [],
      repositories: refuse,
    },
  };
}
